import { TFunction } from "i18next";
import moment from "moment";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import { useGetBusinessPartnerUsersQuery } from "../../../app/services/chatbot-rtk.services";
import ApiFailure from "../../../components/ui/ApiFailure";
import ApiLoading from "../../../components/ui/ApiLoading";
import LoadingComponent from "../../../components/common/loading/loading.component";
import { Button, Row } from "react-bootstrap";
import Alert from "../../../components/common/Alert";
import { AlertsType } from "../../../constants/alerts.constant";

const userReadablePlan: Record<string, string> = {
  CHATBOT_FREE: "FREE",
  CHABOTC_LSH3RKBR5W: "FREE",
  CHATBOT_BASIC_MONTHLY: "BASIC MONTHLY",
  CHABOM_YXN1LILRWY: "BASIC MONTHLY",
  CHATBOT_BASIC_ANNUAL: "BASIC ANNUAL",
  CHABOA_02OW2E8C4U: "BASIC ANNUAL",
  CHATBOT_BASIC_ANNUAL_TRIAL: "BASIC ANNUAL",
  CHATBOT_ADVANCED_MONTHLY: "ADVANCED MONTHLY",
  CHABOM_OSMFCYDT0V: "ADVANCED MONTHLY",
  CHATBOT_ADVANCED_ANNUAL: "ADVANCED ANNUAL",
  CHABOA_B42CDBJP56: "ADVANCED ANNUAL",
  CHATBOT_ADVANCED_ANNUAL_TRIAL: "ADVANCED ANNUAL",
  CHABOA_Y8O3FH61GW: "ENTERPRISE ANNUAL",
  CHABOM_5Q5FNDRELE: "ENTERPRISE ANNUAL",
  CHABOM_TX5B3V1J9V: "ENTERPRISE MONTHLY",
  CHABOM_8F83AHUURU: "ENTERPRISE MONTHLY",
  LB_APPSUMO_TIER1: "APPSUMO TIER 1",
  LB_APPSUMO_TIER2: "APPSUMO TIER 2",
  LB_APPSUMO_TIER3: "APPSUMO TIER 3 "
};

const planStatusReadable: Record<string, string> = {
  NOT_PAYABLE: "NOT PAYABLE",
  PAID: "PAID",
  PAYABLE: "ELIGIBLE",
  PROVISIONAL: "PROVISIONAL",
  NOT_FINALIZED: "NOT FINALIZED",
  NOT_ELIGIBLE: "NOT ELIGIBLE"
};

export const renderStatusBadge = (
  status: string,
  t: TFunction<"translation", undefined>
) => {
  const types: Record<string, React.ReactNode> = {
    waiting_scan: (
      <div className="tw-w-max tw-h-4 tw-px-4 tw-py-0.5 tw-bg-sky-100 tw-rounded-[10px] tw-justify-center tw-items-center tw-inline-flex tw-ml-3">
        <div className="tw-text-center tw-text-sky-800 tw-text-xs tw-font-medium tw-font-['Inter'] tw-leading-none">
          {t("General/UserPage")}
        </div>
      </div>
    ),
    ready: (
      <div className="tw-w-max tw-h-4 tw-px-4 tw-py-0.5 tw-bg-emerald-100 tw-rounded-[10px] tw-justify-center tw-items-center tw-inline-flex tw-ml-3">
        <div className="tw-text-center tw-text-emerald-800 tw-text-xs tw-font-medium tw-font-['Inter'] tw-leading-none">
          {t("General/key5")}
        </div>
      </div>
    ),
    failed: (
      <div className="tw-w-max tw-h-4 tw-px-4 tw-py-0.5 tw-bg-red-100 tw-rounded-[10px] tw-justify-center tw-items-center tw-inline-flex tw-ml-3">
        <div className="tw-text-center tw-text-red-800 tw-text-xs tw-font-medium tw-font-['Inter'] tw-leading-none">
          {t("General/key6")}
        </div>
      </div>
    ),
    updating_bot: (
      <div className="tw-w-max tw-h-4 tw-px-4 tw-py-0.5 tw-bg-amber-100 tw-rounded-[10px] tw-justify-center tw-items-center tw-inline-flex tw-ml-3">
        <div className="tw-text-center tw-text-amber-800 tw-text-xs tw-font-medium tw-font-['Inter'] tw-leading-none">
          {t("General/key7")}
        </div>
      </div>
    ),
    scan_in_progress: (
      <div className="tw-w-max tw-h-4 tw-px-4 tw-py-0.5 tw-bg-sky-100 tw-rounded-[10px] tw-justify-center tw-items-center tw-inline-flex tw-ml-3">
        <div className="tw-text-center tw-text-sky-800 tw-text-xs tw-font-medium tw-font-['Inter'] tw-leading-none">
          {t("General/key9")}
        </div>
      </div>
    ),
    disabled: (
      <div className="tw-w-max tw-h-4 tw-px-4 tw-py-0.5 tw-bg-red-100 tw-rounded-[10px] tw-justify-center tw-items-center tw-inline-flex tw-ml-3">
        <div className="tw-text-center tw-text-red-800 tw-text-xs tw-font-medium tw-font-['Inter'] tw-leading-none">
          {t("General/key13")}
        </div>
      </div>
    )
  };

  return types[status];
};

const PartnerUserTable = () => {
  const { t } = useTranslation();

  const [paginationInfo, setPaginationInfo] = useState({
    limit: 20,
    offset: 1,
    days: 30
  });

  const { isLoading, isError, isSuccess, refetch, data, isFetching } =
    useGetBusinessPartnerUsersQuery(paginationInfo);

  const handleNextPage = () => {
    setPaginationInfo((prev) => ({
      ...prev,
      offset: prev.offset + 1
    }));
  };

  const handlePrevPage = () => {
    setPaginationInfo((prev) => ({
      ...prev,
      offset: prev.offset - 1
    }));
  };

  let content;
  let mobileContent;

  if (isError) {
    content = (
      <Row className=" tw-w-5/6 tw-mx-auto tw-mt-2">
        <Alert type="Error">
          <div className="tw-flex tw-items-center tw-gap-4">
            <p className="m-0">{t(AlertsType.ISKBKO003)}</p>
            <Button size="sm" className="px-3" onClick={() => refetch()}>
              {t("KnowledgeBasesCreateConditionalScenario/btn-01")}
            </Button>
          </div>
        </Alert>
      </Row>
    );
  }

  if (isLoading || isFetching) return <LoadingComponent />;

  if (isSuccess) {
    content = (
      <React.Fragment>
        <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-flex tw-w-full tw-overflow-auto">
          {/* Headers */}
          <div className="tw-self-stretch tw-justify-start tw-items-start tw-inline-flex tw-border-0 tw-border-solid tw-border-b tw-border-gray-200">
            <div className="sm:tw-basis-2/5 tw-basis-5/5 lg:tw-shrink tw-shrink-0 tw-h-10 tw-px-6 tw-py-3 tw-bg-gray-50 tw-rounded-tl-lg tw-border-b tw-border-gray-200 tw-justify-start tw-items-center tw-flex">
              <div className="tw-text-gray-500 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                {t("BusinessPartner/UserPage/id")}
              </div>
            </div>
            <div className="sm:tw-basis-2/5 tw-basis-5/5 lg:tw-shrink tw-shrink-0 tw-h-10 tw-px-6 tw-py-3 tw-bg-gray-50 tw-rounded-tl-lg tw-border-b tw-border-gray-200 tw-justify-start tw-items-center tw-flex">
              <div className="tw-text-gray-500 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                {t("BusinessPartner/UserPage/plan")}
              </div>
            </div>
            <div className="sm:tw-basis-2/5 tw-basis-5/5 lg:tw-shrink tw-shrink-0 tw-h-10 tw-px-6 tw-py-3 tw-bg-gray-50 tw-rounded-tl-lg tw-border-b tw-border-gray-200 tw-justify-start tw-items-center tw-flex">
              <div className="tw-text-gray-500 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                {t("BusinessPartner/UserPage/registrationDate")}
              </div>
            </div>
            <div className="sm:tw-basis-2/5 tw-basis-5/5 lg:tw-shrink tw-shrink-0 tw-h-10 tw-px-6 tw-py-3 tw-bg-gray-50 tw-rounded-tl-lg tw-border-b tw-border-gray-200 tw-justify-start tw-items-center tw-flex">
              <div className="tw-text-gray-500 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                {t("BusinessPartner/UserPage/status")}
              </div>
            </div>
          </div>
          {isLoading ? (
            <div className="tw-self-stretch tw-px-6 tw-py-1 tw-bg-white tw-border-0 tw-border-solid tw-border-b tw-border-gray-200 tw-justify-center tw-items-center tw-gap-4 tw-inline-flex">
              <ApiLoading />
            </div>
          ) : isError ? (
            <div className="tw-self-stretch tw-px-6 tw-py-1 tw-bg-white tw-border-0 tw-border-solid tw-border-b tw-border-gray-200 tw-justify-center tw-items-center tw-gap-4 tw-inline-flex">
              <ApiFailure refetch={refetch} />
            </div>
          ) : !data?.users || !data?.metaData ? (
            <React.Fragment>
              <div className="tw-self-stretch tw-py-1 tw-bg-white tw-justify-center tw-items-center tw-text-gray-700 tw-inline-flex">
                {t("BusinessPartner/UserPage/noUser")}
              </div>
            </React.Fragment>
          ) : (
            <React.Fragment>
              {data?.users.map((user: Record<string, any>, index: number) => (
                <div
                  key={index}
                  className="tw-self-stretch tw-justify-start tw-items-start tw-inline-flex sm:tw-flex-row tw-flex-col tw-border-0 tw-border-solid tw-border-b tw-border-gray-200 tw-bg-white"
                >
                  <div className="sm:tw-basis-2/5 tw-basis-5/5 lg:tw-shrink tw-shrink-0 tw-h-10 tw-px-6 tw-py-3 tw-bg-white tw-rounded-tl-lg tw-border-b tw-border-gray-200 tw-justify-start tw-items-center tw-flex">
                    <div className="tw-text-gray-500 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                      {user.userId}
                    </div>
                  </div>
                  <div className="sm:tw-basis-2/5 tw-basis-5/5 lg:tw-shrink tw-shrink-0 tw-h-10 tw-px-6 tw-py-3 tw-bg-white tw-rounded-tl-lg tw-border-b tw-border-gray-200 tw-justify-start tw-items-center tw-flex">
                    <div className="tw-text-gray-500 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                      {userReadablePlan[user.plan]}
                    </div>
                  </div>
                  <div className="sm:tw-basis-2/5 tw-basis-5/5 lg:tw-shrink tw-shrink-0 tw-h-10 tw-px-6 tw-py-3 tw-bg-white tw-rounded-tl-lg tw-border-b tw-border-gray-200 tw-justify-start tw-items-center tw-flex">
                    <div className="tw-text-gray-500 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                      {moment(user.registerationDateTime).format(
                        "YYYY-MM-DD HH:mm"
                      )}
                    </div>
                  </div>
                  <div className="sm:tw-basis-2/5 tw-basis-5/5 lg:tw-shrink tw-shrink-0 tw-h-10 tw-px-6 tw-py-3 tw-bg-white tw-rounded-tl-lg tw-border-b tw-border-gray-200 tw-justify-start tw-items-center tw-flex">
                    <div className="tw-text-gray-500 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                      {user?.paymentDetail?.paymentStatus === "PAYABLE" && (
                        <div
                          className={`tw-h-6 tw-px-3 tw-py-0.5 tw-bg-emerald-100 tw-rounded-xl tw-justify-center tw-items-center tw-inline-flex`}
                        >
                          <div className="tw-text-center tw-text-emerald-800 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                            {
                              planStatusReadable[
                                user?.paymentDetail?.paymentStatus
                              ]
                            }
                          </div>
                        </div>
                      )}
                      {user?.paymentDetail?.paymentStatus === "PAID" && (
                        <div
                          className={`tw-h-6 tw-px-3 tw-py-0.5 tw-bg-gray-400 tw-rounded-xl tw-justify-center tw-items-center tw-inline-flex`}
                        >
                          <div className="tw-text-center tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                            {
                              planStatusReadable[
                                user?.paymentDetail?.paymentStatus
                              ]
                            }
                          </div>
                        </div>
                      )}
                      {user?.paymentDetail?.paymentStatus === "NOT_PAYABLE" && (
                        <div
                          className={`tw-h-6 tw-px-3 tw-py-0.5 tw-bg-orange-300 tw-rounded-xl tw-justify-center tw-items-center tw-inline-flex`}
                        >
                          <div className="tw-text-center tw-text-red-800 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                            {t(
                              `BusinessPartner/UserPage/${user?.paymentDetail?.paymentStatus}`
                            )}
                          </div>
                        </div>
                      )}
                      {(user?.paymentDetail?.paymentStatus === "NOT_ELIGIBLE" ||
                        user?.paymentDetail?.paymentStatus === "PROVISIONAL" ||
                        user?.paymentDetail?.paymentStatus ===
                          "NOT_FINALIZED") && (
                        <div
                          className={`tw-h-6 tw-px-3 tw-py-0.5 tw-bg-orange-300 tw-rounded-xl tw-justify-center tw-items-center tw-inline-flex`}
                        >
                          <div className="tw-text-center tw-text-red-800 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                            {t(
                              `BusinessPartner/UserPage/${user?.paymentDetail?.paymentStatus}`
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </React.Fragment>
          )}
        </div>
        {data?.metaData && (
          <div className="tw-flex tw-justify-between tw-items-center tw-p-4 tw-border-t tw-border-gray-200">
            <div className="tw-flex tw-items-center  tw-gap-2 tw-ml-4">
              <button
                className={`tw-border-none tw-rounded-md tw-font-medium tw-transition-colors tw-duration-200 ${
                  !data?.metaData.hasPreviousePage
                    ? "tw-bg-gray-100 tw-text-gray-400 tw-cursor-not-allowed"
                    : "tw-bg-blue-500 tw-text-white hover:tw-bg-primary active:tw-bg-blue-700 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-400 focus:tw-ring-offset-2"
                }`}
                disabled={!data?.metaData.hasPreviousePage}
                onClick={handlePrevPage}
              >
                {t("BusinessPartner/UserPage/previous")}
              </button>

              <span className="tw-px-3 tw-py-1 tw-text-sm tw-text-gray-700">
                Page {data?.metaData.page}/
                {Math.round(data?.metaData.totalPage)}
              </span>

              <button
                className={`tw-border-none tw-rounded-md tw-font-medium tw-transition-colors tw-duration-200 ${
                  !data?.metaData.hasNextPage
                    ? "tw-bg-gray-100 tw-text-gray-400 tw-cursor-not-allowed"
                    : "tw-bg-blue-500 tw-text-white hover:tw-bg-primary active:tw-bg-blue-700 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-400 focus:tw-ring-offset-2"
                }`}
                disabled={!data?.metaData.hasNextPage}
                onClick={handleNextPage}
              >
                {t("BusinessPartner/UserPage/next")}
              </button>
            </div>
          </div>
        )}
      </React.Fragment>
    );
    mobileContent = (
      <React.Fragment>
        {/* Headers */}
        <div className="tw-h-10 tw-px-6 tw-py-3 tw-bg-gray-50 tw-rounded-tl-2xl tw-rounded-tr-2xl tw-border-b tw-border-gray-200 tw-justify-start tw-items-center tw-inline-flex">
          <div className="tw-text-gray-500 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
            {t("BusinessPartner/UserPage/info")}
          </div>
        </div>
        {isLoading ? (
          <div className="tw-self-stretch tw-px-6 tw-py-1 tw-bg-white tw-border-0 tw-border-solid tw-border-b tw-border-gray-200 tw-justify-center tw-items-center tw-gap-4 tw-inline-flex">
            <ApiLoading />
          </div>
        ) : isError ? (
          <div className="tw-self-stretch tw-px-6 tw-py-1 tw-bg-white tw-border-0 tw-border-solid tw-border-b tw-border-gray-200 tw-justify-center tw-items-center tw-gap-4 tw-inline-flex">
            <ApiFailure refetch={refetch} />
          </div>
        ) : !data?.users || !data?.metaData ? (
          <React.Fragment>
            <div className="tw-self-stretch tw-py-1 tw-px-3 tw-bg-white tw-justify-center tw-items-center tw-text-gray-700 tw-inline-flex">
              {t("BusinessPartner/UserPage/noUser")}
            </div>
          </React.Fragment>
        ) : (
          <React.Fragment>
            {data?.users.map((user: Record<string, any>, index: number) => (
              <div className="tw-flex tw-flex-col">
                <div
                  key={user.userId}
                  className="tw-bg-white tw-h-40 tw-px-6 tw-py-4 tw-flex-col tw-justify-center tw-items-start tw-gap-3 tw-inline-flex"
                >
                  <div className="tw-self-stretch tw-justify-between tw-items-center tw-inline-flex">
                    <div className="tw-text-gray-500 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                      {t("BusinessPartner/UserPage/status")}
                    </div>
                    {user?.paymentDetail?.paymentStatus === "PAYABLE" && (
                      <div
                        className={`tw-h-6 tw-px-3 tw-py-0.5 tw-bg-emerald-100 tw-rounded-xl tw-justify-center tw-items-center tw-inline-flex`}
                      >
                        <div className="tw-text-center tw-text-emerald-800 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                          {
                            planStatusReadable[
                              user?.paymentDetail?.paymentStatus
                            ]
                          }
                        </div>
                      </div>
                    )}
                    {user?.paymentDetail?.paymentStatus === "PAID" && (
                      <div
                        className={`tw-h-6 tw-px-3 tw-py-0.5 tw-bg-gray-400 tw-rounded-xl tw-justify-center tw-items-center tw-inline-flex`}
                      >
                        <div className="tw-text-center tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                          {
                            planStatusReadable[
                              user?.paymentDetail?.paymentStatus
                            ]
                          }
                        </div>
                      </div>
                    )}
                    {user?.paymentDetail?.paymentStatus === "NOT_PAYABLE" && (
                      <div
                        className={`tw-h-6 tw-px-3 tw-py-0.5 tw-bg-orange-300 tw-rounded-xl tw-justify-center tw-items-center tw-inline-flex`}
                      >
                        <div className="tw-text-center tw-text-red-800 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                          {t(
                            `BusinessPartner/UserPage/${user?.paymentDetail?.paymentStatus}`
                          )}
                        </div>
                      </div>
                    )}
                    {(user?.paymentDetail?.paymentStatus === "NOT_ELIGIBLE" ||
                      user?.paymentDetail?.paymentStatus === "PROVISIONAL" ||
                      user?.paymentDetail?.paymentStatus ===
                        "NOT_FINALIZED") && (
                      <div
                        className={`tw-h-6 tw-px-3 tw-py-0.5 tw-bg-orange-300 tw-rounded-xl tw-justify-center tw-items-center tw-inline-flex`}
                      >
                        <div className="tw-text-center tw-text-red-800 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                          {t(
                            `BusinessPartner/UserPage/${user?.paymentDetail?.paymentStatus}`
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="tw-self-stretch tw-justify-between tw-items-center tw-inline-flex">
                    <div className="tw-text-gray-500 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                      {t("BusinessPartner/UserPage/mobileId")}
                    </div>
                    <div className="tw-text-gray-900 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                      {user.userId}
                    </div>
                  </div>
                  <div className="tw-self-stretch tw-justify-between tw-items-center tw-inline-flex">
                    <div className="tw-text-gray-500 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                      {t("BusinessPartner/UserPage/plan")}
                    </div>
                    <div className="tw-text-gray-900 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                      {userReadablePlan[user.plan]}
                    </div>
                  </div>
                  <div className="tw-self-stretch tw-justify-between tw-items-center tw-inline-flex">
                    <div className="tw-text-gray-500 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                      {t("BusinessPartner/UserPage/regDate")}
                    </div>
                    <div className="tw-text-gray-900 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                      {moment(user.registerationDateTime).format(
                        "MMM DD, YYYY"
                      )}
                    </div>
                  </div>
                </div>
                <hr className="tw-m-0 tw-bg-white border-t border-gray-200" />
              </div>
            ))}
          </React.Fragment>
        )}
        {data?.metaData && (
          <div className="tw-rounded-b-3xl tw-bg-white w-h-16 tw-px-4 tw-py-3 tw-justify-between tw-items-start tw-inline-flex">
            <button
              className={`tw-bg-white tw-rounded-md tw-shadow-sm tw-border tw-border-gray-300 tw-justify-center tw-items-center tw-flex tw-overflow-hidden tw-text-sm tw-font-medium tw-font-['Inter'] tw-leading-tight  tw-px-4 tw-py-2 ${
                data?.metaData.hasPreviousePage
                  ? "tw-text-gray-700"
                  : "tw-cursor-not-allowed"
              }`}
              disabled={!data?.metaData.hasPreviousePage}
              onClick={handlePrevPage}
            >
              {t("BusinessPartner/UserPage/previous")}
            </button>
            <button
              className={`tw-bg-white tw-rounded-md tw-shadow-sm tw-border tw-border-gray-300 tw-justify-center tw-items-center tw-flex tw-overflow-hidden tw-text-sm tw-font-medium tw-font-['Inter'] tw-leading-tight  tw-px-4 tw-py-2 ${
                data?.metaData.hasNextPage
                  ? "tw-text-gray-700 "
                  : "tw-cursor-not-allowed"
              }`}
              disabled={!data?.metaData.hasNextPage}
              onClick={handleNextPage}
            >
              {t("BusinessPartner/UserPage/next")}
            </button>
          </div>
        )}
      </React.Fragment>
    );
  }

  return (
    <>
      <div className=" tw-hidden tw-w-full tw-shadow tw-flex-col tw-justify-start tw-items-start tw-gap-3 lg:tw-inline-flex tw-bg-white">
        {content}
      </div>
      <div className="tw-flex tw-flex-col tw-px-4 tw-my-3 lg:tw-hidden">
        {mobileContent}
      </div>
    </>
  );
};
export default PartnerUserTable;
